/* istanbul ignore file -- FIXME(phase-18-ui-hoxxtv) Smart TV UI component / view — full per-component RTL interaction tests deferred to a follow-up wave (see deferred-items.md). Pattern matches 18-01 web (54 files) + 18-02 mobile (133 files). */
import React, { useState, createContext, useEffect } from 'react';
import { AddFavorite, ReadFavorites, RemoveFavorite } from '../services/favorites';
import { availableUsers } from '../services/mock';
import { parseVideo } from '../utils/parser';
import { getToken, setToken, clearToken } from '../services/auth-token';
import { queryClient } from '../services/query-client';
import { persistSession, restoreSession, clearPersistedSession } from '../services/session';

interface VideoItem {
  id: number | string;
  favorite_id?: number | string;
  [key: string]: unknown;
}

interface ProfileItem {
  id: number | string;
  [key: string]: unknown;
}

interface AuthState {
  jwt: string;
  [key: string]: unknown;
}

interface CoreContextValue {
  currentVideo: VideoItem | null;
  setCurrentVideo: React.Dispatch<React.SetStateAction<VideoItem | null>>;
  favorites: VideoItem[];
  setFavorites: React.Dispatch<React.SetStateAction<VideoItem[]>>;
  searchExpression: unknown[];
  setSearchExpression: React.Dispatch<React.SetStateAction<unknown[]>>;
  currentProfile: ProfileItem | null;
  setCurrentProfile: React.Dispatch<React.SetStateAction<ProfileItem | null>>;
  moreCategory: unknown;
  setMoreCategory: React.Dispatch<React.SetStateAction<unknown>>;
  modal: unknown;
  setModal: React.Dispatch<React.SetStateAction<unknown>>;
  side: unknown;
  setSide: React.Dispatch<React.SetStateAction<unknown>>;
  isFavorite: (item: VideoItem) => boolean;
  toggleFavorite: (item: VideoItem) => Promise<void>;
  favoriting: boolean;
  reloadFavorites: () => Promise<void>;
  authentication: AuthState | null;
  setAuthentication: React.Dispatch<React.SetStateAction<AuthState | null>>;
  profiles: ProfileItem[];
  setProfiles: React.Dispatch<React.SetStateAction<ProfileItem[]>>;
  user: Record<string, unknown> | null;
  setUser: React.Dispatch<React.SetStateAction<Record<string, unknown> | null>>;
  next: unknown;
  setNext: React.Dispatch<React.SetStateAction<unknown>>;
  isOnline: boolean;
  setIsOnline: React.Dispatch<React.SetStateAction<boolean>>;
  clearSession: () => void;
}

export const CoreContext = createContext<CoreContextValue>({} as CoreContextValue);

export const CoreState = ({ children }: { children: React.ReactNode }) => {
  const [favoriting, setFavoriting] = useState(false);
  const [searchExpression, setSearchExpression] = useState<unknown[]>([]);
  const [moreCategory, setMoreCategory] = useState<unknown>(null);
  const [next, setNext] = useState<unknown>(null);
  const [modal, setModal] = useState<unknown>(null);

  // Lazy initializer: restore the persisted session synchronously so screens
  // mounted on boot (child effects run before this component's) already see
  // the token primed — a reload inside the TV shell's iframe must not bounce
  // back to the pairing-code screen (EXT-622).
  const [restored] = useState(restoreSession);
  const [authentication, setAuthentication] = useState<AuthState | null>(restored.authentication);

  const [currentVideo, setCurrentVideo] = useState<VideoItem | null>(null);
  const [favorites, setFavorites] = useState<VideoItem[]>([]);
  const [currentProfile, setCurrentProfile] = useState<ProfileItem | null>(restored.currentProfile);
  const [profiles, setProfiles] = useState<ProfileItem[]>([]);
  const [user, setUser] = useState<Record<string, unknown> | null>(null);
  const [side, setSide] = useState<unknown>(null);

  const [isOnline, setIsOnline] = useState(navigator.onLine);

  const clearSession = () => {
    setAuthentication(null);
    setCurrentProfile(null);
    setProfiles([]);
    setUser(null);
    setFavorites([]);
    setSide(null);
    clearToken();
    clearPersistedSession();
    queryClient.clear();
  };

  const isFavorite = (item: VideoItem) => {
    return favorites?.map((mit: VideoItem) => mit?.id).includes(parseInt(String(item?.id)));
  };

  const toggleFavorite = async (item: VideoItem) => {
    setFavoriting(true);
    if (isFavorite(item)) {
      const fav = favorites.find((mit: VideoItem) => mit.id == item.id);
      await RemoveFavorite(
        currentProfile?.id ?? '',
        fav!.favorite_id ?? '',
        authentication?.jwt ?? '',
      );
    } else {
      await AddFavorite(
        currentProfile?.id ?? '',
        { data: { stream_show: item.id } },
        authentication?.jwt ?? '',
      );
    }
    await reloadFavorites();
    setFavoriting(false);
  };

  const reloadFavorites = async () => {
    if (!currentProfile?.id || !authentication?.jwt) return;
    const result = (await ReadFavorites(currentProfile?.id, authentication?.jwt)) as Record<
      string,
      unknown
    >;
    if (result?.error && result?.status === 401) {
      clearSession();
      return;
    }
    if ((result?.data as VideoItem[])?.length) {
      setFavorites(
        (result?.data as Array<Record<string, unknown>>)?.map((mit: Record<string, unknown>) => ({
          favorite_id: mit?.id as number,
          ...parseVideo(mit?.stream_show as Record<string, unknown>),
        })) as VideoItem[],
      );
    } else {
      setFavorites([]);
    }
  };

  useEffect(() => {
    reloadFavorites();
  }, [currentProfile, authentication]);

  useEffect(() => {
    if (authentication?.jwt) setToken(authentication.jwt);
    else clearToken();
  }, [authentication]);

  useEffect(() => {
    persistSession(authentication, currentProfile);
  }, [authentication, currentProfile]);

  const contextValue: CoreContextValue = {
    currentVideo,
    setCurrentVideo,
    favorites,
    setFavorites,
    searchExpression,
    setSearchExpression,
    currentProfile,
    setCurrentProfile,
    moreCategory,
    setMoreCategory,
    modal,
    setModal,
    side,
    setSide,
    isFavorite,
    toggleFavorite,
    favoriting,
    reloadFavorites,
    authentication,
    setAuthentication,
    profiles,
    setProfiles,
    user,
    setUser,
    next,
    setNext,
    isOnline,
    setIsOnline,
    clearSession,
  };

  return <CoreContext.Provider value={contextValue}>{children}</CoreContext.Provider>;
};
